import React from 'react';
import { api, ApiError } from '../api';
import { Field, StepProgress } from '../ui';
import { EARLY_RELEASE_NOTICE_PARAGRAPHS } from './EarlyRelease';
import { AuthDivider, consumeAuthError, PROVIDER_LABELS, ProviderIcon, SocialSignInButtons, type SignInProvider } from './SocialSignIn';

type View = 'signin' | 'create' | 'inbox' | 'verifying' | 'verified' | 'forgot' | 'reset';
type EmailVerificationResponse = { authenticated: boolean; workerName?: string; studentId?: string };

/** Public account creation only; guided assistant setup continues after sign-in. */
const ONBOARDING_STEPS = ['Create parent account', 'Verify email'];

const initialForm = {
  parentName: '',
  email: '',
  password: '',
};

export type PublicConfig = {
  productName: string;
  operatorName: string;
  websiteUrl: string;
  supportEmail: string;
  termsUrl: string;
  privacyUrl: string;
  earlyReleaseDisclaimerVersion: string;
  signInProviders?: SignInProvider[];
};

type SocialSignup = { provider: SignInProvider; email: string | null; name: string | null; emailVerified: boolean };

const DEFAULT_PUBLIC_CONFIG: PublicConfig = {
  productName: 'Student Assistant',
  operatorName: '[PLACEHOLDER: operator name]',
  websiteUrl: 'http://localhost:8080',
  supportEmail: 'studentassistantsupport@gmail.com',
  termsUrl: '/terms',
  privacyUrl: '/privacy',
  earlyReleaseDisclaimerVersion: '2026-09-29-v1',
};

export function AuthView({ onAuth }: { onAuth: () => void }) {
  const params = new URLSearchParams(window.location.search);
  const isReset = window.location.pathname === '/reset-password';
  const token = isReset ? null : params.get('token');
  const resetToken = isReset ? params.get('token') : null;
  const [view, setView] = React.useState<View>(resetToken ? 'reset' : token ? 'verifying' : window.location.pathname === '/create-account' ? 'create' : window.location.pathname === '/forgot-password' ? 'forgot' : 'signin');
  const [form, setForm] = React.useState(initialForm);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = React.useState('');
  const [devVerifyUrl, setDevVerifyUrl] = React.useState<string | null>(null);
  const [worker, setWorker] = React.useState<{ name: string; studentId: string } | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = React.useState('');
  const [config, setConfig] = React.useState<PublicConfig>(DEFAULT_PUBLIC_CONFIG);
  const [resetEmail, setResetEmail] = React.useState('');
  const [resetPassword, setResetPassword] = React.useState('');
  const [resetSent, setResetSent] = React.useState(false);
  const [devResetUrl, setDevResetUrl] = React.useState<string | null>(null);
  const [social, setSocial] = React.useState<SocialSignup | null>(null);
  const verificationRequest = React.useRef<{ token: string; promise: Promise<EmailVerificationResponse> } | null>(null);
  const providers = config.signInProviders ?? [];

  React.useEffect(() => {
    const authError = consumeAuthError();
    if (authError) setError(authError);
    if (!params.get('with')) return;
    fetch('/api/auth/oidc/signup', { credentials: 'same-origin' })
      .then((response) => response.ok ? response.json() : null)
      .then((value) => {
        if (value?.available) {
          setSocial(value);
          setForm((current) => ({ ...current, email: value.email ?? '', parentName: current.parentName || value.name || '' }));
        } else {
          setError('Your Google or Microsoft sign-in expired. Please continue with it again.');
        }
      })
      .catch(() => undefined);
  }, []);

  const cancelSocial = async () => {
    await fetch('/api/auth/oidc/signup', { method: 'DELETE', credentials: 'same-origin' }).catch(() => undefined);
    setSocial(null);
    setForm((current) => ({ ...current, email: '' }));
    window.history.replaceState({}, '', '/create-account');
  };

  React.useEffect(() => {
    fetch('/api/public-config')
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('public config unavailable')))
      .then((value: PublicConfig) => setConfig(value))
      .catch(() => undefined);
  }, []);

  const go = (next: View, path: string) => {
    window.history.pushState({}, '', path);
    setView(next);
    setError(null);
  };

  React.useEffect(() => {
    if (!token) return;
    let active = true;
    setBusy(true);
    if (verificationRequest.current?.token !== token) {
      verificationRequest.current = {
        token,
        promise: api<EmailVerificationResponse>('POST', '/api/auth/verify-email', { token }),
      };
    }
    verificationRequest.current.promise
      .then((res) => {
        if (!active) return;
        if (res.authenticated) {
          setWorker(res.workerName && res.studentId
            ? { name: res.workerName, studentId: res.studentId }
            : null);
          setView('verified');
          window.history.replaceState({}, '', '/verified');
        } else {
          setView('signin');
          setError('Your email is verified. Sign in to continue.');
        }
      })
      .catch((e) => {
        if (!active) return;
        setView('verifying');
        setError(e.message);
      })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [token]);

  const signIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null); setUnverifiedEmail('');
    try {
      await api('POST', '/api/auth/login', { email: form.email, password: form.password });
      onAuth();
    } catch (e) {
      const err = e as ApiError;
      if (err.code === 'email_unverified') {
        setUnverifiedEmail(form.email);
        setError(`Verify ${form.email} before signing in.`);
      } else if (err.code === 'invalid_credentials') {
        setError('The email or password is incorrect.');
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const register = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await api<any>('POST', '/api/auth/register', {
        accountOnly: true,
        parentName: form.parentName,
        email: social ? undefined : form.email,
        password: social ? undefined : form.password,
      });
      if (res.authenticated) {
        setWorker(res.workerName && res.studentId
          ? { name: res.workerName, studentId: res.studentId }
          : null);
        setView('verified');
        window.history.replaceState({}, '', '/verified');
        return;
      }
      setMaskedEmail(res.verification.maskedEmail);
      setDevVerifyUrl(res.verification.devVerifyUrl ?? null);
      setView('inbox');
      window.history.replaceState({}, '', '/check-email');
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async (email = form.email) => {
    setBusy(true); setError(null);
    try {
      const res = await api<any>('POST', '/api/auth/resend-verification', { email });
      setMaskedEmail(res.maskedEmail);
      setDevVerifyUrl(res.devVerifyUrl ?? null);
      setView('inbox');
      window.history.replaceState({}, '', '/check-email');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const requestReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null); setResetSent(false); setDevResetUrl(null);
    try {
      const res = await api<any>('POST', '/api/auth/request-password-reset', { email: resetEmail });
      setResetSent(true);
      setDevResetUrl(res.devResetUrl ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!resetToken) return;
    setBusy(true); setError(null);
    try {
      await api('POST', '/api/auth/reset-password', { token: resetToken, password: resetPassword });
      setView('signin');
      setError('Password reset. Sign in with your new password.');
      window.history.replaceState({}, '', '/');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (view === 'create') {
    return (
      <AuthShell eyebrow="Parent onboarding" title="Create your parent account" step={0}
        supportEmail={config.supportEmail}
        description="Create your parent account first. After verification, add your child in the Family Command Center and complete their guided setup.">
        <form onSubmit={register} className="auth-form">
          {!social && providers.length > 0 && (
            <>
              <SocialSignInButtons providers={providers} verb="Sign up" />
              <AuthDivider label="or sign up with email" />
            </>
          )}
          {social && (
            <div className="banner ok social-signup-banner">
              <ProviderIcon provider={social.provider} />
              <div>
                <strong>Signing up with {PROVIDER_LABELS[social.provider]}</strong>
                <div>{social.email}. No password needed. {social.emailVerified
                  ? 'Your email is already verified.'
                  : 'We will email you a link to confirm this address.'}</div>
                <button type="button" className="link-button" onClick={cancelSocial}>Use email and password instead</button>
              </div>
            </div>
          )}
          <fieldset>
            <legend>Parent or guardian</legend>
            <div className="form-grid">
              <Field label="Parent or guardian full name" value={form.parentName}
                onChange={(parentName) => setForm({ ...form, parentName })} required />
              {!social && (
                <>
                  <Field label="Parent email" type="email" value={form.email}
                    onChange={(email) => setForm({ ...form, email })} required />
                  <Field label="Password" type="password" value={form.password}
                    onChange={(password) => setForm({ ...form, password })}
                    placeholder="At least 10 characters" required />
                </>
              )}
            </div>
          </fieldset>
          <p className="trust-note">
            No child profile or phone number is created here. Add your child after signing in; the student phone is required there and verified before reminders can be sent. Permissions and SMS choices are collected separately in Family setup. Review our{' '}
            <a href={config.termsUrl} target="_blank" rel="noreferrer">Terms of Service</a> and{' '}
            <a href={config.privacyUrl} target="_blank" rel="noreferrer">Privacy Policy</a>.
          </p>
          {error && <div className="banner bad" role="alert"><strong>We could not create the account.</strong><div>{error}</div></div>}
          <button className="primary auth-primary" disabled={busy} type="submit">
            {busy ? 'Creating account…' : 'Create parent account'}
          </button>
          <p className="auth-switch">Already have an account? <button type="button" className="link-button" onClick={() => go('signin', '/')}>Sign in</button></p>
        </form>
      </AuthShell>
    );
  }

  if (view === 'inbox') {
    return (
      <AuthShell eyebrow="Email verification" title="Check your inbox" step={1}
        supportEmail={config.supportEmail}
        description={`We sent a verification link to ${maskedEmail || form.email}. Open it within 30 minutes to verify your parent account. Then continue to Family setup to add your child and complete setup.`}>
        <div className="status-illustration" aria-hidden="true">✉</div>
        <p className="trust-note">You will stay on this step until your email is verified. If the message does not arrive, check spam or request another email.</p>
        {error && <div className="banner bad" role="alert">{error}</div>}
        <button className="primary auth-primary" disabled={busy} onClick={() => resend()}>
          {busy ? 'Sending…' : 'Resend verification email'}
        </button>
        {devVerifyUrl && <a className="dev-verification" href={devVerifyUrl}>Development only: open verification link</a>}
        <button className="link-button" onClick={() => go('create', '/create-account')}>Change email or account details</button>
      </AuthShell>
    );
  }

  if (view === 'verifying') {
    return (
      <AuthShell eyebrow="Email verification" title={busy ? 'Verifying your email…' : 'Verification link unavailable'} step={1}
        supportEmail={config.supportEmail}
        description={busy ? 'Please wait while we verify your email and create your parent account.' : 'Request a new verification email from the sign-in page.'}>
        {error && <div className="banner bad" role="alert">{error}</div>}
        {!busy && <button className="primary auth-primary" onClick={() => go('signin', '/')}>Return to sign in</button>}
      </AuthShell>
    );
  }

  if (view === 'forgot') {
    return (
      <AuthShell eyebrow="Account recovery" title="Reset your password"
        supportEmail={config.supportEmail}
        description="Enter your parent account email and we will send a one-time password reset link.">
        <form onSubmit={requestReset} className="auth-form">
          <Field label="Parent email" type="email" value={resetEmail} onChange={setResetEmail} required />
          {error && <div className="banner bad" role="alert">{error}</div>}
          {resetSent && <div className="banner ok">If an account exists for that email, a reset link has been sent.</div>}
          {devResetUrl && <a className="dev-verification" href={devResetUrl}>Development only: open reset link</a>}
          <button className="primary auth-primary" disabled={busy} type="submit">{busy ? 'Sending…' : 'Send reset link'}</button>
          <button type="button" className="link-button" onClick={() => go('signin', '/')}>Return to sign in</button>
        </form>
      </AuthShell>
    );
  }

  if (view === 'reset') {
    return (
      <AuthShell eyebrow="Account recovery" title="Choose a new password"
        supportEmail={config.supportEmail}
        description="Use at least 10 characters. This link can only be used once and expires after 30 minutes.">
        <form onSubmit={submitReset} className="auth-form">
          <Field label="New password" type="password" value={resetPassword} onChange={setResetPassword} required />
          {error && <div className="banner bad" role="alert">{error}</div>}
          <button className="primary auth-primary" disabled={busy} type="submit">{busy ? 'Saving…' : 'Save new password'}</button>
        </form>
      </AuthShell>
    );
  }

  if (view === 'verified') {
    return (
      <AuthShell eyebrow="Continue setup" title="Account created"
        supportEmail={config.supportEmail}
        description={worker
          ? `${worker.name} has been created. Continue to the Family Command Center to finish setup. The assistant will not activate until required permissions, Canvas connection and first sync, and student phone verification are complete.`
          : 'Your parent account is verified. Continue to the Family Command Center to add a child profile; no child or assistant has been created yet.'}>
        <div className="banner ok">
          <strong>{worker ? 'Family account created' : 'Parent account verified'}</strong>
          <div>{worker
            ? 'Your child’s Student Assistant is ready for guided setup.'
            : 'Your next step is to add your first child in Family setup.'}</div>
        </div>
        {worker ? (
          <ol className="next-steps-list">
            <li>
              <strong>Review permissions.</strong>
              <p>Confirm guardian, school-data, SMS, and AI permissions. These permissions are separate from phone verification.</p>
            </li>
            <li>
              <strong>Connect the school.</strong>
              <p>Connect the student’s read-only Canvas account and complete its first sync. No assignments need to exist yet.</p>
            </li>
            <li>
              <strong>Verify your child’s phone number.</strong>
              <p>Verify the mobile number or watch that will receive reminders.</p>
            </li>
            <li>
              <strong>Activate the assistant.</strong>
              <p>Once permissions, Canvas sync, and phone verification are complete, you can turn on reminders.</p>
            </li>
          </ol>
        ) : (
          <p className="muted">
            Add your child’s profile and required phone number in Family setup. Permissions, Canvas connection and first sync, phone verification, and activation are handled there as separate steps.
          </p>
        )}
        <button className="primary auth-primary" onClick={onAuth}>Continue to Family Command Center</button>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="Family Command Center" title="Welcome back"
      supportEmail={config.supportEmail}
      description="Sign in to manage your children’s Student Assistants, school connections, and notification preferences.">
      {providers.length > 0 && (
        <>
          <SocialSignInButtons providers={providers} />
          <AuthDivider label="or sign in with email" />
        </>
      )}
      <form onSubmit={signIn} className="auth-form">
        <Field label="Parent email" type="email" value={form.email}
          onChange={(email) => setForm({ ...form, email })} required />
        <Field label="Password" type="password" value={form.password}
          onChange={(password) => setForm({ ...form, password })} required />
        {error && (
          <div className="banner bad" role="alert">
            <strong>{unverifiedEmail ? 'Verify your email to continue' : 'Sign-in failed'}</strong>
            <div>{error}</div>
            {unverifiedEmail && <button type="button" onClick={() => resend(unverifiedEmail)} disabled={busy}>Resend verification email</button>}
          </div>
        )}
        <button className="primary auth-primary" disabled={busy} type="submit">{busy ? 'Signing in…' : 'Sign in'}</button>
        <button type="button" className="link-button" onClick={() => go('forgot', '/forgot-password')}>Forgot password?</button>
      </form>
      <div className="auth-new-account">
        <strong>New to the Student Assistant?</strong>
        <p>Create a parent account, then add a child in Family setup.</p>
        <button onClick={() => go('create', '/create-account')}>Create parent account</button>
      </div>
      <p className="trust-note">Designed for families. School access is read-only, and notification controls remain with the parent or guardian.</p>
    </AuthShell>
  );
}

export function PolicyView({ kind }: { kind: 'terms' | 'privacy' }) {
  const privacy = kind === 'privacy';
  const [config, setConfig] = React.useState<PublicConfig>(DEFAULT_PUBLIC_CONFIG);
  React.useEffect(() => {
    fetch('/api/public-config')
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('public config unavailable')))
      .then((value: PublicConfig) => setConfig(value))
      .catch(() => undefined);
  }, []);
  return (
    <main className="auth-shell policy-shell">
      <section className="auth-brand">
        <a className="brand-lockup brand-home-link" aria-label="Student Assistant home page" href="https://studentassistant.net/">
          <span className="brand-mark" aria-hidden="true">SA</span>
          <span className="brand-wordmark">Student Assistant</span>
        </a>
        <p className="eyebrow">{config.productName}</p>
        <h1>{privacy ? 'Privacy Policy' : 'Terms of Service'}</h1>
        <p className="auth-description">Pilot policy information for families using the service.</p>
      </section>
      <section className="auth-panel policy-content">
        <p><a href="/create-account">Back to sign up</a></p>
        <div className="banner">
          <strong>Draft policy for pilot review</strong>
          <p>Before launch, replace unresolved operator, website, support, jurisdiction, retention, and contact details with approved final language.</p>
        </div>
        {privacy ? (
          <>
            <h2>What we use</h2>
            <p>{config.operatorName} uses account, student, school-connection, and verified mobile information to operate {config.productName}, reminders, and the Family Command Center.</p>
            <h2>Google and Microsoft sign-in</h2>
            <p>If a parent chooses Google or Microsoft sign-in, we receive the provider account name, email address, email-verification status, and a stable account identifier. The parent confirms their name during signup, and we save the parent name and email on the family account for sign-in, email verification, and account recovery. We never receive the provider password or access to that account’s email, files, or calendar through sign-in; a class-calendar connection requires separate authorization. Provider sign-in does not grant SMS consent or subscribe the parent to promotional messages. A parent can connect or disconnect sign-in methods in Account settings, but cannot remove the last usable method.</p>
            <h2>Consent records</h2>
            <p>When a parent accepts the Early Release Notice, we store the notice version and the server-recorded acceptance time with the family consent record.</p>
            <h2>Your choices</h2>
            <p>Parents control school connections, notification settings, opt-out requests, and account deletion. Reply STOP to unsubscribe from SMS messages, START to resubscribe when eligible, and HELP for assistance.</p>
            <h2>Sharing</h2>
            <p><strong>We do not share, sell, rent, or provide mobile phone numbers, SMS opt-in data, or messaging consent to third parties or affiliates for marketing or promotional purposes.</strong></p>
            <p>We may disclose information to service providers solely as needed to operate the application and deliver messages requested by the user. These providers may not use mobile information or messaging consent for their own marketing or promotional purposes. Questions: <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>.</p>
          </>
        ) : (
          <>
            <h2>Using the service</h2>
            <p>You are responsible for providing accurate information and for confirming that you are authorized to manage the student account.</p>
            <h2>SMS program</h2>
            <p>Messages are automated reminders and updates. Message frequency varies based on activity and settings. Message and data rates may apply. Reply HELP for help, STOP to unsubscribe, or START to resubscribe when eligible.</p>
            <h2>Pilot status</h2>
            <p>This service is currently operated as a family pilot by {config.operatorName}. Features may change as the service is improved.</p>
            <section id="early-release">
              <h2>Early-release service and limitations</h2>
              {EARLY_RELEASE_NOTICE_PARAGRAPHS.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              <p><strong>Draft for legal review:</strong> a qualified lawyer should review this wording, including the liability and family-responsibility language, before a broader release. Nothing here removes legal duties or limits rights that applicable law does not allow us to waive.</p>
            </section>
          </>
        )}
      </section>
    </main>
  );
}

function AuthShell(props: {
  eyebrow: string;
  title: string;
  description: string;
  step?: number;
  supportEmail?: string;
  children: React.ReactNode;
}) {
  const supportEmail = props.supportEmail ?? DEFAULT_PUBLIC_CONFIG.supportEmail;
  return (
    <main className="auth-shell">
      <section className="auth-brand">
        <nav className="auth-public-nav" aria-label="Public sections">
          <a href="/faq">FAQ</a>
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
        </nav>
        <a className="brand-lockup brand-home-link" aria-label="Student Assistant home page" href="https://studentassistant.net/">
          <span className="brand-mark" aria-hidden="true">SA</span>
          <span className="brand-wordmark">Student Assistant</span>
        </a>
        <p className="eyebrow">{props.eyebrow}</p>
        <h1>{props.title}</h1>
        <p className="auth-description">{props.description}</p>
      </section>
      <section className="auth-panel">
        {props.step !== undefined && <StepProgress steps={ONBOARDING_STEPS} currentIndex={props.step} />}
        {props.children}
        <p className="auth-support">Need help? <a href={`mailto:${supportEmail}`}>{supportEmail}</a></p>
      </section>
    </main>
  );
}
